import { useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Database, FileCheck, AlertTriangle, CheckCircle2, XCircle, Download, Upload, FileSpreadsheet, Pencil } from 'lucide-react';
import EditableRegister from '@/components/register/EditableRegister';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILE_TYPES = [
  {
    id: 'lines', num: 1, label: 'Line BoQ', desc: 'Master piping line register',
    exportType: 'lines', importType: 'lines', registerId: 'pwm.lines',
    importHelp: 'Required: Line ID, AG/UG, Area. Optional: Description, Fab Location, Priority, Work Pack, Total Inch-Dia.',
  },
  {
    id: 'isometrics', num: 2, label: 'Isometric Index', desc: 'Drawing index with inch-meter',
    exportType: 'isometrics', importType: 'isometrics', registerId: 'pwm.isometrics',
    importHelp: 'Required: Drawing ID. Optional: Line ID, AG/UG, Area, Fab Location, Inch-Dia, Inch-Meter.',
  },
  {
    id: 'area-priority', num: 3, label: 'Area Details + Priority', desc: 'Area/priority assignments',
    exportType: 'area-priority', importType: 'area-priority', registerId: 'pwm.area-priority',
    importHelp: 'Required: Area. Optional: Work Pack, Fab Location, Priority (1-5), Sub Priority. Applies priority to matching lines.',
  },
  {
    id: 'po-details', num: 4, label: 'PO Details', desc: 'Purchase order & material receipts',
    exportType: 'po-details', importType: 'po-details', registerId: 'pwm.po-details',
    importHelp: 'Required: PO Number, SAP Code. Optional: Description, Ordered Qty, Received Qty, Delivery Date, Status.',
  },
  {
    id: 'spool-boq', num: 5, label: 'Spool BoQ', desc: 'Spool bill of quantities',
    exportType: 'spool-boq', importType: 'spool-boq', registerId: 'pwm.spool-boq',
    importHelp: 'Required: Spool No. Optional: Drawing ID, Line ID, AG/UG, Area, Spool Type, Fab Location, Shop/Field/Total Inch-Dia, Inch-Meter, Surface Area.',
  },
  {
    id: 'stage-gates', num: 6, label: 'Spool at a Glance', desc: 'Stage gate dates & progress',
    exportType: 'spools', importType: 'stage-gates', registerId: 'pwm.stage-gates',
    importHelp: 'Required: Spool No. Optional date columns: Fit-Up Date, Welding Date, NDT Date, Painting Date, Dispatch Date, Erection Date. Optional report columns: Fit-Up Report, etc.',
  },
];

export default function PWMDataImport({ projectId }) {
  const [seeding, setSeeding] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(null);
  const [importing, setImporting] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [editRegister, setEditRegister] = useState(null);
  const fileRefs = useRef({});

  const seedAll = async () => {
    setSeeding(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/pwm/seed-all`);
      toast.success(res.data?.message || 'Data seeded successfully');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
    setSeeding(false);
  };

  const runValidation = async () => {
    setValidating(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/pwm/validate`);
      setValidationResults(res.data);
      toast.success('Validation complete');
    } catch (e) { toast.error('Validation failed'); }
    setValidating(false);
  };

  const handleExport = async (exportType, label) => {
    setExporting(exportType);
    try {
      const url = `${API_URL}/api/v1/projects/${projectId}/pwm/export/${exportType}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PWM_${label.replace(/\s+/g, '_')}_${projectId}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${label} to Excel`);
    } catch (e) { toast.error(`Export failed: ${e.message}`); }
    setExporting(null);
  };

  const handleTemplateDownload = async (importType, label) => {
    setDownloadingTemplate(importType);
    try {
      const url = `${API_URL}/api/v1/projects/${projectId}/pwm/templates/${importType}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Template download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PWM_${label.replace(/\s+/g, '_')}_Template.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${label} template`);
    } catch (e) { toast.error(`Template download failed: ${e.message}`); }
    setDownloadingTemplate(null);
  };

  const handleImport = async (importType, file) => {
    if (!file) return;
    setImporting(importType);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/v1/projects/${projectId}/pwm/import/${importType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult({ type: importType, ...res.data });
      toast.success(res.data?.message || 'Import complete');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
      setImportResult({ type: importType, error: e?.response?.data?.detail || 'Import failed' });
    }
    setImporting(null);
  };

  const statusIcon = (status) => {
    if (status === 'PASS') return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
    if (status === 'WARNING') return <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />;
    return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  };

  return (
    <div className="space-y-4" data-testid="pwm-data-import">
      {/* All 6 Source Files — Import & Export */}
      <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="w-4 h-4" style={{ color: '#143A6B' }} />
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Registers — Edit In-Platform + Excel Import / Export</h4>
        </div>
        <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>Click <b>Edit Rows</b> on any register to open a full data grid — add, edit, delete rows directly. Excel import/export remains available as a bulk-load fallback.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {FILE_TYPES.map(ft => (
            <div key={ft.id} className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}
              data-testid={`file-card-${ft.id}`}>
              {/* Card Header */}
              <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                  style={{ background: '#143A6B20', color: '#143A6B' }}>{ft.num}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{ft.label}</p>
                  <p className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }}>{ft.desc}</p>
                </div>
              </div>
              {/* Import hint */}
              <p className="px-3 py-1 text-[8px] leading-tight" style={{ color: 'var(--text-muted)', background: 'var(--card-bg)' }}>{ft.importHelp}</p>
              {/* Buttons */}
              <div className="flex gap-1.5 p-2" style={{ borderTop: '1px solid var(--border-color)' }}>
                <Button size="sm" variant="outline" onClick={() => handleTemplateDownload(ft.importType, ft.label)}
                  disabled={downloadingTemplate === ft.importType} data-testid={`template-${ft.id}-btn`}
                  className="text-[9px] h-7 px-2" title="Download blank import template">
                  {downloadingTemplate === ft.importType ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleExport(ft.exportType, ft.label)}
                  disabled={exporting === ft.exportType} data-testid={`export-${ft.id}-btn`}
                  className="text-[9px] h-7 px-2" title="Export to Excel">
                  {exporting === ft.exportType ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                </Button>
                <input type="file" accept=".xlsx,.xls" className="hidden"
                  ref={el => { fileRefs.current[ft.id] = el; }}
                  onChange={e => { handleImport(ft.importType, e.target.files?.[0]); e.target.value = ''; }} />
                <Button size="sm" variant="outline" onClick={() => fileRefs.current[ft.id]?.click()}
                  disabled={importing === ft.importType} data-testid={`import-${ft.id}-btn`}
                  className="text-[9px] h-7 px-2" title="Bulk import from Excel">
                  {importing === ft.importType ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                </Button>
                <Button size="sm" onClick={() => setEditRegister(ft.registerId)}
                  data-testid={`edit-rows-${ft.id}-btn`}
                  className="flex-1 text-[9px] h-7" style={{ background: '#143A6B' }} title="Edit rows in-platform">
                  <Pencil className="w-3 h-3 mr-0.5" /> Edit Rows
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <div className="p-3 rounded-lg border" style={{ borderColor: importResult.error ? '#ef4444' : '#22c55e',
          background: importResult.error ? '#ef444410' : '#22c55e10' }} data-testid="import-result">
          {importResult.error ? (
            <p className="text-xs font-medium" style={{ color: '#ef4444' }}>{importResult.error}</p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs font-semibold" style={{ color: '#22c55e' }}>{importResult.message}</p>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {importResult.imported != null && `New: ${importResult.imported}`}
                {importResult.updated != null && ` | Updated: ${importResult.updated}`}
                {importResult.notFound != null && ` | Not Found: ${importResult.notFound}`}
                {importResult.linesUpdated != null && ` | Lines Updated: ${importResult.linesUpdated}`}
              </p>
              {importResult.errors?.length > 0 && (
                <div className="mt-1">
                  <p className="text-[10px] font-medium" style={{ color: '#f59e0b' }}>Warnings ({importResult.errors.length}):</p>
                  {importResult.errors.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Seed + Validate Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Seed Demo Data</h4>
          <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
            Generate 50 lines, ~340 spools, 30 POs, 100+ isometric drawings with realistic allocation and progress data.
          </p>
          <Button size="sm" onClick={seedAll} disabled={seeding} data-testid="seed-demo-btn">
            {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Database className="w-3.5 h-3.5 mr-1" />}
            Seed All Demo Data
          </Button>
        </div>

        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Cross-File Validation</h4>
          <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
            Run DV-01 through DV-08 validation checks across all imported data files.
          </p>
          <Button size="sm" variant="outline" onClick={runValidation} disabled={validating} data-testid="run-validation-btn">
            {validating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileCheck className="w-3.5 h-3.5 mr-1" />}
            Run Validation Checks
          </Button>
        </div>
      </div>

      {/* Validation Results */}
      {validationResults && (
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Validation Results</h4>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{
              background: validationResults.overallStatus === 'CLEAN' ? '#22c55e20' : '#f59e0b20',
              color: validationResults.overallStatus === 'CLEAN' ? '#22c55e' : '#f59e0b'
            }}>{validationResults.overallStatus}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(validationResults.validationResults || []).map(vr => (
              <div key={vr.checkId} className="flex items-start gap-2 p-2 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                {statusIcon(vr.status)}
                <div>
                  <p className="text-[10px] font-bold" style={{ color: 'var(--text-primary)' }}>{vr.checkId}</p>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{vr.description}</p>
                  {vr.failCount > 0 && <p className="text-[9px] font-bold" style={{ color: '#ef4444' }}>{vr.failCount} issues</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editable Register Sheet */}
      <Sheet open={!!editRegister} onOpenChange={o => !o && setEditRegister(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[92vw] p-0 flex flex-col">
          <SheetHeader className="sr-only">
            <SheetTitle>Edit Register Rows</SheetTitle>
          </SheetHeader>
          {editRegister && <EditableRegister projectId={projectId} registerId={editRegister} onClose={() => setEditRegister(null)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
